import type { JSONContent, MarkdownRendererHelpers } from "@tiptap/core"

export const BLOCK_SEPARATOR = "\n\n"

/**
 * Recursively extracts plain text from a ProseMirror / Tiptap node.
 */
export function nodePlainText(node?: JSONContent): string {
  if (!node) return ""
  if (node.text) return node.text
  return (node.content || []).map(nodePlainText).join("")
}

/**
 * Extracts plain text from a list of blocks, joining blocks with double newlines.
 */
export function blocksPlainText(blocks?: JSONContent[]): string {
  if (!blocks) return ""
  return blocks.map(nodePlainText).filter(Boolean).join(BLOCK_SEPARATOR)
}

/**
 * Renders child nodes formatted with standard Markdown paragraph breaks (\n\n).
 */
export function childrenWithLineBreaks(
  helpers: MarkdownRendererHelpers,
  children: JSONContent[],
): string {
  return helpers.renderChildren(children, BLOCK_SEPARATOR)
}

/**
 * Template string tag for renderMarkdown functions.
 *
 * Lets extension templates follow source indentation without adding it to Markdown.
 * Empty interpolations disappear; whitespace is normalized outside fenced code.
 * Values must already be Markdown, since this tag does not escape their contents.
 */
export function md(template: TemplateStringsArray, ...values: unknown[]): string {
  let minIndent = Infinity
  for (const part of template) {
    const lines = part.split(/\r?\n/)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]
      if (line.trim().length > 0) {
        const indent = line.match(/^[ \t]*/)?.[0].length ?? 0
        if (indent < minIndent) minIndent = indent
      }
    }
  }
  if (minIndent === Infinity) minIndent = 0

  let raw = ""
  for (let i = 0; i < template.length; i++) {
    const part = template[i]
    const lines = part.split(/\r?\n/)
    const dedentedPart = lines
      .map((line, idx) => {
        if (idx === 0 && i > 0) return line
        return line.slice(minIndent)
      })
      .join("\n")
    raw += dedentedPart

    if (i < values.length) {
      const val = values[i]
      if (val !== undefined && val !== null && val !== false) {
        raw += String(val)
      }
    }
  }

  let inCodeFence = false
  const lines = raw.split(/\r?\n/).map((line) => {
    const trimmed = line.trim()
    if (trimmed.startsWith("```")) {
      inCodeFence = !inCodeFence
      return line.trimEnd()
    }
    if (inCodeFence) {
      return line.trimEnd()
    }

    const match = line.match(/^([ \t]*)(.*)$/)
    const leading = match ? match[1] : ""
    const rest = (match ? match[2] : line).replace(/[ \t]{2,}/g, " ").trimEnd()
    // Tiptap emits two trailing spaces for hardBreak; trimming them joins the lines.
    const hardBreak = / {2,}$/.test(line) ? "  " : ""
    return leading + rest + hardBreak
  })

  return lines
    .join("\n")
    .replace(/\n{3,}/g, BLOCK_SEPARATOR)
    .trim()
}

export function mdLink(text: string, url: string): string {
  return `[${text}](<${escapeMarkdownUrl(url)}>)`
}

export function mdImage(alt: string, src: string): string {
  return `![${alt}](<${escapeMarkdownUrl(src)}>)`
}

function escapeMarkdownUrl(url: string): string {
  return String(url).replaceAll("\\", "\\\\").replaceAll(">", "\\>")
}
